import { type Editor } from '@tiptap/react';
import { Bold, Italic, Underline, Heading1, Heading2, Undo } from 'lucide-react';
import { useAppStore } from '@/lib/store';
import { cn } from '@/lib/utils';
import { useEffect, useState } from 'react';

interface MobileFormatBarProps {
    editor: Editor | null;
}

export function MobileFormatBar({ editor }: MobileFormatBarProps) {
    const { themeProfile } = useAppStore();
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        if (!editor) return;

        const handleFocus = () => setIsVisible(true);
        const handleBlur = () => {
            // Delay hiding slightly to allow button clicks to register
            setTimeout(() => {
                if (!editor.isFocused) setIsVisible(false);
            }, 100);
        };

        editor.on('focus', handleFocus);
        editor.on('blur', handleBlur);

        return () => {
            editor.off('focus', handleFocus);
            editor.off('blur', handleBlur);
        };
    }, [editor]);

    if (!editor || !isVisible) return null;

    const buttons = [
        {
            icon: Undo,
            action: () => editor.chain().focus().undo().run(),
            isActive: false,
            disabled: !editor.can().undo(),
        },
        {
            icon: Bold,
            action: () => editor.chain().focus().toggleBold().run(),
            isActive: editor.isActive('bold'),
            disabled: false,
        },
        {
            icon: Italic,
            action: () => editor.chain().focus().toggleItalic().run(),
            isActive: editor.isActive('italic'),
            disabled: false,
        },
        {
            icon: Underline,
            action: () => editor.chain().focus().toggleUnderline().run(),
            isActive: editor.isActive('underline'),
            disabled: false,
        },
        {
            icon: Heading1,
            action: () => editor.chain().focus().toggleHeading({ level: 1 }).run(),
            isActive: editor.isActive('heading', { level: 1 }),
            disabled: false,
        },
        {
            icon: Heading2,
            action: () => editor.chain().focus().toggleHeading({ level: 2 }).run(),
            isActive: editor.isActive('heading', { level: 2 }),
            disabled: false,
        },
    ];

    return (
        <div
            className="fixed bottom-0 left-0 right-0 z-50 p-2 transform transition-transform animate-in slide-in-from-bottom"
            style={{ backgroundColor: themeProfile.paperSurface, borderTop: `1px solid ${themeProfile.border}` }}
        >
            <div className="flex items-center justify-between overflow-x-auto gap-2 px-2 pb-safe-area hide-scrollbar">
                {buttons.map((btn, i) => {
                    const Icon = btn.icon;
                    return (
                        <button
                            key={i}
                            onClick={(e) => {
                                e.preventDefault();
                                btn.action();
                            }}
                            disabled={btn.disabled}
                            className={cn(
                                "p-2.5 rounded-xl flex-shrink-0 transition-colors",
                                btn.isActive ? "shadow-inner" : "",
                                btn.disabled ? "opacity-30" : "active:scale-95"
                            )}
                            style={{
                                backgroundColor: btn.isActive ? `${themeProfile.text}15` : 'transparent',
                                color: btn.isActive ? themeProfile.accent : themeProfile.text
                            }}
                        >
                            <Icon className="w-5 h-5" strokeWidth={btn.isActive ? 2.5 : 2} />
                        </button>
                    );
                })}
            </div>
        </div>
    );
}
